import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Download, Loader2, RefreshCw, X } from 'lucide-react';
import type { GbuFassungInhalt } from '../../../services/sifa/gbuTypes';
import { describeError } from '../../../services/errorMessage';
import { istNativeApp } from '../../../services/nativePlatform';
import { NativePdfSeiten } from '../../NativePdfSeiten';
import { type GbuAusgabeOpt, baueGbuPdf, gbuPdfBlobAusgeben } from './gbuAusgabe';

interface Props {
  inhalt: GbuFassungInhalt;
  opt: GbuAusgabeOpt;
  /** Hinweis oben (z. B. „nicht alles gespeichert"). */
  hinweis?: string | null;
  onClose: () => void;
}

/**
 * Vorschau des ECHTEN PDF (gbuPdf.erzeugeGbuPdf) — keine HTML-Nachbildung:
 * was hier steht, kommt auch aus dem Download. Entwurf mit Wasserzeichen
 * „ENTWURF", sonst die eingefrorene bzw. freigegebene Fassung.
 *
 * Muster UfsaPdfPreview: Laufnummer gegen den Wettlauf (eine aeltere,
 * langsamere Erzeugung ueberschreibt nie eine neuere), die Blob-URL wird nur
 * beim Ersetzen und beim Schliessen freigegeben. In der Android-App gibt es
 * keinen PDF-Betrachter in der WebView — dort zeigt NativePdfSeiten die Seiten.
 */
export const GbuVorschauDialog: React.FC<Props> = ({ inhalt, opt, hinweis = null, onClose }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(true);
  const [laedtHerunter, setLaedtHerunter] = useState(false);
  const [lauf, setLauf] = useState(0);
  const urlRef = useRef<string | null>(null);
  const blobRef = useRef<Blob | null>(null);
  const genRef = useRef(0);
  const nativ = istNativeApp();

  useEffect(() => {
    const meine = ++genRef.current;
    void baueGbuPdf(inhalt, opt)
      .then((blob) => {
        if (meine !== genRef.current) return;
        blobRef.current = blob;
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        const neu = URL.createObjectURL(blob);
        urlRef.current = neu;
        setUrl(neu);
        setFehler(null);
      })
      .catch((e: unknown) => {
        if (meine !== genRef.current) return;
        setFehler(describeError(e));
      })
      .finally(() => {
        if (meine === genRef.current) setLaeuft(false);
      });
  }, [inhalt, opt, lauf]);

  // Beim Schliessen: letzte Blob-URL freigeben und eine laufende Erzeugung entwerten.
  useEffect(() => () => {
    genRef.current++;
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
  }, []);

  const herunterladen = async () => {
    const blob = blobRef.current;
    if (!blob || laedtHerunter) return;
    setLaedtHerunter(true);
    try {
      await gbuPdfBlobAusgeben(inhalt, blob, !!opt.entwurf);
    } catch (e) {
      setFehler(describeError(e));
    } finally {
      setLaedtHerunter(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[95vh] flex flex-col">
        <div className="p-3 border-b border-slate-200 flex items-center justify-between gap-2 shrink-0">
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-slate-800">Vorschau — GBU Nr. {inhalt.nummer}, Fassung {inhalt.fassung}{opt.entwurf ? ' (Entwurf)' : ''}</h3>
            <p className="text-xs text-slate-500 truncate">{inhalt.titel}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => { setLaeuft(true); setLauf((n) => n + 1); }} disabled={laeuft} className="min-h-[40px] px-3 flex items-center gap-1.5 text-xs font-bold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg disabled:opacity-40">
              <RefreshCw className="w-3.5 h-3.5" /> Neu erzeugen
            </button>
            <button onClick={() => void herunterladen()} disabled={!url || laedtHerunter} className="min-h-[40px] px-3 flex items-center gap-1.5 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-40">
              {laedtHerunter ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} PDF herunterladen
            </button>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full"><X className="w-4 h-4 text-slate-500" /></button>
          </div>
        </div>
        {hinweis && <p className="px-3 py-2 text-xs font-bold text-amber-900 bg-amber-50 border-b border-amber-200">{hinweis}</p>}
        <div className="relative flex-1 min-h-0 bg-slate-100 rounded-b-2xl overflow-hidden">
          {url && nativ && (
            <NativePdfSeiten key={url} pdfUrl={url} onTeilen={() => void herunterladen()} teilenLaeuft={laedtHerunter} teilenText="PDF herunterladen" />
          )}
          {url && !nativ && <iframe title="PDF-Vorschau der Gefährdungsbeurteilung" src={url} className="w-full h-full border-0" />}
          {fehler && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 bg-slate-100">
              <AlertTriangle className="w-9 h-9 mb-3 text-amber-500" />
              <p className="font-bold text-slate-700 mb-1">Vorschau konnte nicht erstellt werden</p>
              <p className="text-xs text-slate-500 break-words max-w-md">{fehler}</p>
            </div>
          )}
          {laeuft && !fehler && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-500 pointer-events-none">
              <Loader2 className="w-8 h-8 animate-spin mb-2 text-blue-600" />
              <p className="text-sm font-bold">Vorschau wird erzeugt …</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
